import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, retry, throwError, timer } from 'rxjs';
import { AuthActions } from '../auth/store/auth.actions';
import { environment } from '../../../environments/environment';
import { NotificationService } from '../services/notification.service';

/**
 * Set on a request that shows its own error (e.g. the Location upload's inline error,
 * Figma 503-162449) so its 4xx / 5xx isn't also toasted. 401 / 403 handling still
 * runs, and the error is still re-thrown. Usage:
 * `http.post(url, body, { context: new HttpContext().set(SKIP_ERROR_TOAST, true) })`.
 */
export const SKIP_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

/**
 * Global HTTP error handling (fail-closed, SECURITY-15):
 *  - 401 from the API → sessionExpired: clear all local auth state, go to /signin
 *  - 403 → toast the server's denial message (if any), then navigate to /unauthorized
 *  - 503 → retry idempotent (GET) requests up to 2× with 1s backoff
 *  - other 4xx → toast the server's error message (generic fallback)
 *  - 5xx → toast a generic message only (never the server body, which may leak internals)
 * This is the SINGLE place that toasts HTTP errors — effects/components must not
 * also toast, to avoid a double notification. The original error is always
 * re-thrown so callers/effects can still react (map to a failure action, etc.).
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const store = inject(Store);
  const router = inject(Router);
  const notification = inject(NotificationService);

  return next(req).pipe(
    // Retry only idempotent GETs: a 503 after a PUT/POST may have been applied
    // server-side, so blindly retrying a mutation risks a duplicate write.
    retry({
      count: 2,
      delay: (error: HttpErrorResponse) =>
        error.status === 503 && req.method === 'GET' ? timer(1000) : throwError(() => error),
    }),
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        // Every API 401 means the token (and the Curity session it's validated
        // against) has expired: clear everything locally and sign in again. Only our
        // API's 401s — not the IdP's (e.g. the token exchange), which sign-in handles.
        if (isApiRequest(req.url)) {
          store.dispatch(AuthActions.sessionExpired());
        }
      } else if (error.status === 403) {
        // Surface the server's denial reason (e.g. an IAM explicit-deny message)
        // when present, then still route to the unauthorized page. No generic
        // toast for a bodyless 403 — the page itself conveys the outcome.
        const denialMessage = extractServerMessage(error);
        if (denialMessage) {
          notification.error(denialMessage);
        }
        void router.navigate(['/unauthorized']);
      } else if (!req.context.get(SKIP_ERROR_TOAST)) {
        notification.error(resolveMessage(error));
      }
      return throwError(() => error);
    }),
  );
};

/** Same API scope as the bearer token (authInterceptor) — never matches an empty API_URL. */
function isApiRequest(url: string): boolean {
  const apiUrl = environment.API_URL;
  return !!apiUrl && url.startsWith(apiUrl);
}

function resolveMessage(error: HttpErrorResponse): string {
  if (error.status === 0) {
    return 'Unable to reach the server. Please check your connection and try again.';
  }
  // 5xx: never surface the server body — it can leak internal details (stack
  // traces, driver errors). Always a generic line for server-side failures.
  if (error.status >= 500) {
    return 'Something went wrong on our end. Please try again shortly.';
  }
  // 4xx: prefer the server-provided message so the user sees the real reason
  // (e.g. a validation error), falling back to a generic line when the body
  // carries nothing usable.
  const serverMessage = extractServerMessage(error);
  if (serverMessage) {
    return serverMessage;
  }
  return 'Request failed. Please try again.';
}

/**
 * Pulls a human-readable message out of the error response body across the shapes
 * the compliance API and friends use: FastAPI `{ detail }` (string, or a
 * validation array of `{ msg }`), and `{ message | error | title }`. Key lookup is
 * case-insensitive so PascalCase bodies (AWS/.NET `{ "Message": ... }`) resolve too.
 * Returns null for a missing/non-JSON/HTML body so the caller can fall back to a
 * generic line.
 */
function extractServerMessage(error: HttpErrorResponse): string | null {
  const body: unknown = error.error;

  if (typeof body === 'string') {
    const trimmed = body.trim();
    return trimmed && !trimmed.startsWith('<') && trimmed.length <= 300 ? trimmed : null;
  }
  if (!body || typeof body !== 'object') {
    return null;
  }

  const record = body as Record<string, unknown>;
  // Case-insensitive field lookup — bodies come as camelCase (FastAPI) or
  // PascalCase (AWS/.NET), so match on the lowercased key name.
  const field = (name: string): unknown => {
    const key = Object.keys(record).find((k) => k.toLowerCase() === name);
    return key === undefined ? undefined : record[key];
  };

  const detail = field('detail');
  if (typeof detail === 'string' && detail.trim()) {
    return detail.trim();
  }
  if (Array.isArray(detail)) {
    const first = detail.find(
      (d): d is { msg: string } => typeof (d as { msg?: unknown })?.msg === 'string',
    );
    if (first) {
      return first.msg;
    }
  }
  for (const name of ['message', 'error', 'title']) {
    const value = field(name);
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return null;
}
